import * as React from 'react';
import { createPortal } from 'react-dom';
import type { GuestVoucherDto } from './GuestVoucherCard';
import { ConvertFlow, type VoucherService } from './ConvertFlow';
import type { MassageTypeOption } from '../MassageUsages/MassageTypeSelect';
import { isAuthFailureResponse, logAuthFailure, SESSION_EXPIRED_MESSAGE } from './authFailure';
import { LoadRequestGuard } from './loadRequestGuard';
import { VoucherDetailsPanel } from './VoucherDetailsPanel';
import { VoucherReconcileForm } from './VoucherReconcileForm';
import './VoucherCard.css';

interface VoucherLookupResponse {
    voucher: GuestVoucherDto;
    companyManagerBaseUrl: string | null;
    bookingDateTime: string | null;
    coachName: string | null;
    locationName: string | null;
}

type ModalPhase =
    | { phase: 'loading'; shortCode: string }
    | { phase: 'not-found'; shortCode: string }
    | { phase: 'error'; shortCode: string; message: string }
    | { phase: 'choose-action'; voucher: GuestVoucherDto }
    | { phase: 'reconcile'; voucher: GuestVoucherDto }
    | { phase: 'reconcile-submitting'; voucher: GuestVoucherDto }
    | { phase: 'reconcile-done'; voucher: GuestVoucherDto }
    | { phase: 'convert'; voucher: GuestVoucherDto; service: VoucherService };

export interface ReconcileResult {
    guestVoucherId: string;
    voucherNumber: number;
    shortCode: string;
    personName: string | null;
    voucherType: string;
    massageType: string;
    price: number;
    receiptDate: string;
    redeemedAt: string;
}

export interface VoucherActionModalProps {
    shortCode?: string | null;
    voucher?: GuestVoucherDto | null;
    onClose: () => void;
    onOpenReservation?: (reservationId: string) => void;
    onConverted?: () => void;
    onReconciled?: (result: ReconcileResult) => void;
    companyManagerBaseUrl?: string | null;
    /**
     * When true (default), the overlay renders via createPortal(document.body) so it
     * escapes any ancestor stacking context (e.g. the standalone barcode-scan flow on
     * a bare page). When false, it renders inline in the normal DOM tree instead -
     * required when the caller is itself nested inside a react-bootstrap <Modal>,
     * whose default enforceFocus checks `_contains(modal.dialog, activeElement)` and
     * steals focus back from anything portaled out to document.body as a sibling.
     */
    portal?: boolean;
}

export const VoucherActionModal: React.FC<VoucherActionModalProps> = ({
    shortCode,
    voucher: voucherProp,
    onClose,
    onOpenReservation,
    onConverted,
    onReconciled,
    companyManagerBaseUrl,
    portal = true,
}) => {
    const [state, setState] = React.useState<ModalPhase | null>(null);
    const [services, setServices] = React.useState<VoucherService[]>([]);
    const [loadingServices, setLoadingServices] = React.useState(false);
    const [massageTypes, setMassageTypes] = React.useState<MassageTypeOption[]>([]);

    // Booking details from lookup response
    const [lookupBaseUrl, setLookupBaseUrl] = React.useState<string | null>(null);
    const [bookingDateTime, setBookingDateTime] = React.useState<string | null>(null);
    const [coachName, setCoachName] = React.useState<string | null>(null);
    const [locationName, setLocationName] = React.useState<string | null>(null);

    // Reconcile form state
    const [massageType, setMassageType] = React.useState('');
    const [price, setPrice] = React.useState<number>(0);
    const [receiptDate, setReceiptDate] = React.useState(new Date().toISOString().split('T')[0]);
    const [reconcileError, setReconcileError] = React.useState<string | null>(null);

    const isOpen = !!(shortCode || voucherProp);
    const effectiveBaseUrl = companyManagerBaseUrl ?? lookupBaseUrl;

    const applyLookupResponse = (data: VoucherLookupResponse) => {
        setLookupBaseUrl(data.companyManagerBaseUrl);
        setBookingDateTime(data.bookingDateTime);
        setCoachName(data.coachName);
        setLocationName(data.locationName);
    };

    // Lookup when shortCode changes
    React.useEffect(() => {
        if (!shortCode) return;
        const abortController = new AbortController();
        setState({ phase: 'loading', shortCode });

        fetch(`/api/sales/guest-vouchers/lookup/${encodeURIComponent(shortCode)}`, {
            signal: abortController.signal,
        })
            .then(async (response) => {
                if (!response.ok) {
                    setState({ phase: 'not-found', shortCode });
                    return;
                }
                const data: VoucherLookupResponse = await response.json();
                applyLookupResponse(data);
                setState({ phase: 'choose-action', voucher: data.voucher });
                if (data.voucher.status === 'Active') {
                    loadServicesForVoucher(data.voucher);
                }
            })
            .catch((e) => {
                if ((e as Error).name === 'AbortError') return;
                setState({ phase: 'error', shortCode, message: (e as Error).message });
            });

        return () => abortController.abort();
    }, [shortCode]);

    // Direct voucher prop (from reservation page)
    React.useEffect(() => {
        if (!voucherProp) return;
        if (voucherProp.activeFlowId) {
            setState({ phase: 'convert', voucher: voucherProp, service: { serviceId: '', displayName: '', eligibleVoucherTypes: [], coaches: [], locations: [] } });
        } else {
            setState({ phase: 'choose-action', voucher: voucherProp });
        }
        if (voucherProp.status === 'Active') {
            loadServicesForVoucher(voucherProp);
        }
    }, [voucherProp?.guestVoucherId]);

    // Guards against a slow response for a previously-open voucher landing
    // after a new voucher has already opened and overwriting its services/
    // massageTypes. LoadRequestGuard's id bookkeeping is unit tested in
    // loadRequestGuard.test.mts; the AbortController here additionally
    // cancels the now-irrelevant in-flight requests outright.
    const loadRequestGuardRef = React.useRef(new LoadRequestGuard());
    const loadAbortControllerRef = React.useRef<AbortController | null>(null);

    const loadServicesForVoucher = (voucher: GuestVoucherDto) => {
        loadAbortControllerRef.current?.abort();
        const abortController = new AbortController();
        loadAbortControllerRef.current = abortController;
        const requestId = loadRequestGuardRef.current.start();

        setLoadingServices(true);
        Promise.all([
            fetch(`/api/sales/guest-vouchers/services?voucherType=${encodeURIComponent(voucher.voucherType)}`, { signal: abortController.signal })
                .then((r): Promise<VoucherService[]> => r.ok ? r.json() : Promise.resolve([]))
                .catch((): VoucherService[] => []),
            fetch('/api/sales/guest-vouchers/massage-types', { signal: abortController.signal })
                .then((r): Promise<MassageTypeOption[]> => r.ok ? r.json() : Promise.resolve([]))
                .catch((): MassageTypeOption[] => []),
        ]).then(([svc, types]) => {
            if (!loadRequestGuardRef.current.isCurrent(requestId)) return; // stale voucher - a newer load has since started
            setServices(svc);
            setMassageTypes(types);
        }).finally(() => {
            if (loadRequestGuardRef.current.isCurrent(requestId)) setLoadingServices(false);
        });
    };

    // Close on Escape
    React.useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                const p = state?.phase;
                if (p !== 'reconcile-submitting') onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, state?.phase, onClose]);

    const canClose = state?.phase !== 'reconcile-submitting';

    const handleSelectMassage = () => {
        if (!state || !('voucher' in state)) return;
        setMassageType('');
        setPrice(0);
        setReceiptDate(new Date().toISOString().split('T')[0]);
        setReconcileError(null);
        setState({ phase: 'reconcile', voucher: state.voucher });
    };

    const handleSelectService = (service: VoucherService) => {
        if (!state || !('voucher' in state)) return;
        setState({ phase: 'convert', voucher: state.voucher, service });
    };

    const handleReconcileSubmit = async () => {
        if (!state || state.phase !== 'reconcile') return;
        const { voucher } = state;

        if (!massageType.trim()) {
            setReconcileError('Service type is required.');
            return;
        }

        setState({ phase: 'reconcile-submitting', voucher });
        setReconcileError(null);

        try {
            const response = await fetch('/api/sales/guest-vouchers/reconcile', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify([{
                    guestVoucherId: voucher.guestVoucherId,
                    massageType: massageType.trim(),
                    price,
                    receiptDate,
                }]),
            });

            if (isAuthFailureResponse(response)) {
                logAuthFailure('VoucherActionModal.handleReconcileSubmit', response);
                setReconcileError(SESSION_EXPIRED_MESSAGE);
                setState({ phase: 'reconcile', voucher });
                return;
            }

            if (!response.ok) throw new Error('Reconciliation failed');
            const results = await response.json();
            const result = results[0];

            if (!result.success) {
                setReconcileError(result.error || 'Reconciliation failed');
                setState({ phase: 'reconcile', voucher });
                return;
            }

            setState({ phase: 'reconcile-done', voucher });
            onReconciled?.({
                guestVoucherId: voucher.guestVoucherId,
                voucherNumber: voucher.voucherNumber,
                shortCode: voucher.shortCode,
                personName: voucher.personName,
                voucherType: voucher.voucherType,
                massageType: massageType.trim(),
                price,
                receiptDate,
                redeemedAt: new Date().toISOString(),
            });

            setTimeout(() => {
                onClose();
                setMassageType('');
                setPrice(0);
            }, 1200);
        } catch (e) {
            setReconcileError((e as Error).message);
            setState({ phase: 'reconcile', voucher });
        }
    };

    // After convert success, re-fetch voucher and show the updated (Used) view
    const handleConvertDone = async () => {
        onConverted?.();
        if (!state || !('voucher' in state)) { onClose(); return; }
        const v = state.voucher;
        try {
            const response = await fetch(`/api/sales/guest-vouchers/lookup/${encodeURIComponent(v.shortCode)}`);
            if (response.ok) {
                const data: VoucherLookupResponse = await response.json();
                applyLookupResponse(data);
                setState({ phase: 'choose-action', voucher: data.voucher });
            } else {
                onClose();
            }
        } catch {
            onClose();
        }
    };

    const handleBackToActions = () => {
        if (!state || !('voucher' in state)) return;
        setState({ phase: 'choose-action', voucher: state.voucher });
    };

    if (!isOpen || !state) return null;

    const voucher = 'voucher' in state ? state.voucher : null;
    const isActive = voucher?.status === 'Active';
    const isUsed = voucher?.status === 'Used';
    const typeClass = voucher?.voucherType.toLowerCase() ?? '';
    const statusClass = voucher?.status.toLowerCase() ?? '';

    const overlay = (
        <div className="voucher-scan-overlay" onClick={canClose ? onClose : undefined}>
            <div className="voucher-card" onClick={e => e.stopPropagation()}>

                {/* Close button */}
                {canClose && (
                    <button className="voucher-close-btn" onClick={onClose} title="Close">
                        &#10005;
                    </button>
                )}

                {/* Loading */}
                {state.phase === 'loading' && (
                    <div className="voucher-body" style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="spinner-border" role="status" style={{ width: '2.5rem', height: '2.5rem', color: '#6c757d' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <div style={{ color: '#6c757d', marginTop: '12px' }}>Looking up voucher...</div>
                    </div>
                )}

                {/* Not Found */}
                {state.phase === 'not-found' && (
                    <div className="not-found">
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10060;</div>
                        <h2>Voucher Not Found</h2>
                        <p style={{ color: '#adb5bd', fontSize: '14px' }}>
                            Code <strong>{state.shortCode}</strong> does not match any voucher.
                        </p>
                    </div>
                )}

                {/* Error */}
                {state.phase === 'error' && (
                    <div className="not-found">
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#9888;&#65039;</div>
                        <h2>Lookup Failed</h2>
                        <p style={{ color: '#adb5bd', fontSize: '14px' }}>{state.message}</p>
                    </div>
                )}

                {/* Voucher loaded - header + body shown for all voucher phases */}
                {voucher && (
                    <>
                        {/* Header */}
                        <div className="voucher-header" style={{
                            background: state.phase === 'reconcile-done' ? '#f0fdf4'
                                : isActive ? '#f0fdf4'
                                : '#fef2f2',
                        }}>
                            <h1>Phuket Fit</h1>
                            <div style={{ marginTop: '8px' }}>
                                <span className={`type-badge type-${typeClass}`}>{voucher.voucherType}</span>
                                <span className={`status-badge status-${state.phase === 'reconcile-done' ? 'used' : statusClass}`} style={{ marginLeft: '6px' }}>
                                    {state.phase === 'reconcile-done' ? 'Reconciled' : voucher.status}
                                </span>
                            </div>
                        </div>

                        {/* Reconcile success banner */}
                        {state.phase === 'reconcile-done' && (
                            <div className="converted-banner">
                                <div className="check-icon">&#10003;</div>
                                <div className="converted-text">Reconciled Successfully</div>
                                <div className="converted-subtext">
                                    {massageType} - {price.toLocaleString()} THB
                                </div>
                            </div>
                        )}

                        {/* Voucher details */}
                        {state.phase !== 'convert' && (
                            <VoucherDetailsPanel
                                voucher={voucher}
                                isUsed={isUsed}
                                isReconcileDone={state.phase === 'reconcile-done'}
                                effectiveBaseUrl={effectiveBaseUrl}
                                bookingDateTime={bookingDateTime}
                                coachName={coachName}
                                locationName={locationName}
                            />
                        )}

                        {/* Action selection - Active vouchers only */}
                        {state.phase === 'choose-action' && isActive && (
                            <div className="convert-form">
                                <label>Convert to Service</label>
                                {loadingServices ? (
                                    <div style={{ color: '#6c757d', fontSize: '13px', padding: '8px 0' }}>Loading services...</div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        {services.map(s => (
                                            <button
                                                key={s.serviceId}
                                                onClick={() => handleSelectService(s)}
                                                style={{
                                                    display: 'block', width: '100%', padding: '10px 12px',
                                                    borderRadius: '6px', border: '1px solid #d1d5db',
                                                    background: '#fff', color: '#374151', fontSize: '14px',
                                                    fontWeight: 500, cursor: 'pointer', textAlign: 'left',
                                                }}
                                            >
                                                {s.displayName}
                                            </button>
                                        ))}
                                        <button
                                            onClick={handleSelectMassage}
                                            style={{
                                                display: 'block', width: '100%', padding: '10px 12px',
                                                borderRadius: '6px', border: '1px solid #d1d5db',
                                                background: '#fefce8', color: '#92400e', fontSize: '14px',
                                                fontWeight: 500, cursor: 'pointer', textAlign: 'left',
                                            }}
                                        >
                                            Massage (off-site)
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Reconcile form */}
                        {(state.phase === 'reconcile' || state.phase === 'reconcile-submitting') && (
                            <VoucherReconcileForm
                                massageTypes={massageTypes}
                                massageType={massageType}
                                onMassageTypeChange={setMassageType}
                                onSelectMassageType={(opt) => setPrice(opt.defaultPrice)}
                                price={price}
                                onPriceChange={setPrice}
                                receiptDate={receiptDate}
                                onReceiptDateChange={setReceiptDate}
                                submitting={state.phase === 'reconcile-submitting'}
                                error={reconcileError}
                                onBack={handleBackToActions}
                                onSubmit={handleReconcileSubmit}
                            />
                        )}

                        {/* Convert flow */}
                        {state.phase === 'convert' && (
                            <ConvertFlow
                                voucher={state.voucher}
                                service={state.service}
                                onDone={handleConvertDone}
                                onBack={handleBackToActions}
                            />
                        )}

                        {/* Staff actions - shown on choose-action phase */}
                        {state.phase === 'choose-action' && onOpenReservation && (
                            <div className="staff-actions">
                                <button
                                    className="btn-reservation"
                                    onClick={() => onOpenReservation(voucher.reservationId)}
                                >
                                    Open Reservation
                                </button>
                            </div>
                        )}

                        {/* Footer */}
                        <div className="voucher-footer">
                            Phuket Fit Guest Service Voucher &middot; {voucher.shortCode}
                        </div>
                    </>
                )}

                {/* Close footer for error/not-found */}
                {(state.phase === 'not-found' || state.phase === 'error') && (
                    <div className="voucher-footer" style={{ cursor: 'pointer' }} onClick={onClose}>
                        Click to close
                    </div>
                )}
            </div>
        </div>
    );

    return portal ? createPortal(overlay, document.body) : overlay;
};
